import { useEffect, useMemo, useState } from "react";

const THEME_STORAGE_KEY = "theme"; // 'dark' | 'light'

const reportThemeError = (error) => {
  if (import.meta?.env?.DEV && globalThis.console?.debug) {
    globalThis.console.debug("[theme]", error);
  }
};

const isValidStoredTheme = (value) => value === "dark" || value === "light";

const getStoredTheme = () => {
  if (!("localStorage" in globalThis)) return null;
  try {
    const value = globalThis.localStorage.getItem(THEME_STORAGE_KEY);
    return isValidStoredTheme(value) ? value : null;
  } catch (e) {
    reportThemeError(e);
    return null;
  }
};

const getSystemTheme = () => {
  if (typeof globalThis.matchMedia !== "function") return "light";
  return globalThis.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const applyThemeClass = (theme) => {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
};

export function useTheme() {
  const [theme, setTheme] = useState(() => getStoredTheme() ?? getSystemTheme());

  const isDark = useMemo(() => theme === "dark", [theme]);

  const setThemePreference = (nextTheme) => {
    if (!isValidStoredTheme(nextTheme)) return;

    try {
      if ("localStorage" in globalThis) {
        globalThis.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
      }
    } catch (e) {
      reportThemeError(e);
    }

    setTheme(nextTheme);
  };

  const toggleTheme = () => setThemePreference(isDark ? "light" : "dark");

  // Apply theme class
  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  // If user has not explicitly chosen a theme, follow system changes.
  useEffect(() => {
    const stored = getStoredTheme();
    if (stored) return;
    if (typeof globalThis.matchMedia !== "function") return;

    const media = globalThis.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event) => {
      setTheme(event.matches ? "dark" : "light");
    };

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }

    // Safari < 14
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  return {
    theme,
    isDark,
    setTheme: setThemePreference,
    toggleTheme,
  };
}
